import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import './ShopperAccount.css';

function ShopperAccount() {
  const { user, logout, isShopper, shopperToken } = useAuth();
  const { favorites } = useFavorites();
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Profile editing
  const [profileForm, setProfileForm] = useState({ name: '', email: '', mobile: '' });
  const [profileEdit, setProfileEdit] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // Preferences state - loaded from localStorage
  const [preferences, setPreferences] = useState(() => {
    try {
      const saved = localStorage.getItem('hh_preferences');
      return saved ? JSON.parse(saved) : { emailOrders: true, emailPromos: false, smsOrders: false };
    } catch { return { emailOrders: true, emailPromos: false, smsOrders: false }; }
  });

  // Wallet - saved cards (UI only; actual storage via Stripe Customer)
  const [savedCards, setSavedCards] = useState(() => {
    try {
      const saved = localStorage.getItem('hh_saved_cards');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [addingCard, setAddingCard] = useState(false);
  const [cardForm, setCardForm] = useState({ nickname: '', last4: '', expiry: '', type: 'Visa' });

  // Redirect if not logged in as shopper
  useEffect(() => {
    if (!isShopper()) {
      navigate('/login');
    }
  }, [isShopper, navigate]);

  // Init profile form from user
  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        email: user.email || '',
        mobile: user.mobile || user.phone || ''
      });
    }
  }, [user]);

  // Load orders
  useEffect(() => {
    if (isShopper() && shopperToken) {
      fetchOrders();
    }
  }, [shopperToken]);

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/.netlify/functions/api/orders/my-orders', {
        headers: {
          'Authorization': `Bearer ${shopperToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      setOrders(data);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load your orders');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSaveProfile = (e) => {
    e.preventDefault();
    // In a real implementation, call PATCH /api/users/me with shopperToken
    setProfileEdit(false);
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 3000);
  };

  const handlePreferenceChange = (key) => {
    const updated = { ...preferences, [key]: !preferences[key] };
    setPreferences(updated);
    localStorage.setItem('hh_preferences', JSON.stringify(updated));
  };

  const handleAddCard = (e) => {
    e.preventDefault();
    if (!cardForm.last4 || cardForm.last4.length !== 4 || isNaN(cardForm.last4)) return;
    const newCard = { ...cardForm, id: Date.now() };
    const updated = [...savedCards, newCard];
    setSavedCards(updated);
    localStorage.setItem('hh_saved_cards', JSON.stringify(updated));
    setCardForm({ nickname: '', last4: '', expiry: '', type: 'Visa' });
    setAddingCard(false);
  };

  const handleRemoveCard = (id) => {
    const updated = savedCards.filter(c => c.id !== id);
    setSavedCards(updated);
    localStorage.setItem('hh_saved_cards', JSON.stringify(updated));
  };

  const getTotalSpent = () => {
    return orders
      .filter(order => order.paymentStatus === 'paid')
      .reduce((total, order) => total + order.total, 0);
  };

  const getOrderCount = () => {
    return orders.filter(order => order.paymentStatus === 'paid').length;
  };

  if (!isShopper()) {
    return null;
  }

  return (
    <div className="shopper-account-container">
      <div className="account-header">
        <h1>My Account</h1>
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </div>

      <div className="account-tabs">
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Order History ({getOrderCount()})
        </button>
        <button
          className={`tab ${activeTab === 'favorites' ? 'active' : ''}`}
          onClick={() => setActiveTab('favorites')}
        >
          Favorites ({favorites.length})
        </button>
        <button
          className={`tab ${activeTab === 'preferences' ? 'active' : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          Preferences
        </button>
        <button
          className={`tab ${activeTab === 'wallet' ? 'active' : ''}`}
          onClick={() => setActiveTab('wallet')}
        >
          Wallet
        </button>
      </div>

      <div className="account-content">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="profile-section">
            <div className="profile-card">
              <div className="profile-card-header">
                <h2>Profile Details</h2>
                {!profileEdit && (
                  <button className="edit-profile-btn" onClick={() => setProfileEdit(true)}>Edit</button>
                )}
              </div>
              {profileSaved && <p className="profile-saved-msg">Profile updated successfully!</p>}
              {profileEdit ? (
                <form className="profile-edit-form" onSubmit={handleSaveProfile}>
                  <div className="form-row">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={profileForm.name}
                      onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="form-row">
                    <label>Email Address</label>
                    <input
                      type="email"
                      value={profileForm.email}
                      readOnly
                      className="readonly-input"
                      title="Email cannot be changed here"
                    />
                  </div>
                  <div className="form-row">
                    <label>Mobile Number</label>
                    <input
                      type="tel"
                      value={profileForm.mobile}
                      onChange={e => setProfileForm({ ...profileForm, mobile: e.target.value })}
                      placeholder="+27 XX XXX XXXX"
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="save-profile-btn">Save Changes</button>
                    <button type="button" className="cancel-btn" onClick={() => setProfileEdit(false)}>Cancel</button>
                  </div>
                </form>
              ) : (
                <div className="profile-info">
                  <div className="info-row">
                    <span className="label">Full Name</span>
                    <span className="value">{user?.name || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Email</span>
                    <span className="value">{user?.email}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Mobile</span>
                    <span className="value">{profileForm.mobile || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Member Since</span>
                    <span className="value">
                      {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <h3>{getOrderCount()}</h3>
                <p>Orders Completed</p>
              </div>
              <div className="stat-card">
                <h3>R{(getTotalSpent() || 0).toFixed(2)}</h3>
                <p>Total Spent</p>
              </div>
              <div className="stat-card">
                <h3>{favorites.length}</h3>
                <p>Saved Items</p>
              </div>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="orders-section">
            <h2>Order History</h2>
            {loading ? (
              <p className="loading">Loading orders...</p>
            ) : error ? (
              <p className="error">{error}</p>
            ) : orders.length === 0 ? (
              <div className="empty-state">
                <p>You haven't placed any orders yet.</p>
                <button onClick={() => navigate('/products')} className="continue-shopping">
                  Continue Shopping
                </button>
              </div>
            ) : (
              <div className="orders-list">
                {orders.map((order) => (
                  <div key={order._id} className="order-card">
                    <div className="order-header">
                      <div>
                        <h3>Order #{order.orderNumber}</h3>
                        <p className="order-date">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="order-status">
                        <span className={`status-badge ${order.paymentStatus}`}>
                          {order.paymentStatus.toUpperCase()}
                        </span>
                        <span className={`status-badge ${order.status}`}>
                          {order.status.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <div className="order-items">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="item-row">
                          <span>{item.productName} (x{item.quantity})</span>
                          <span className="price">R{((item.price || 0) * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="order-footer">
                      <div className="order-total">
                        <span>Total:</span>
                        <span className="total-amount">R{(order.total || 0).toFixed(2)}</span>
                      </div>
                      {order.trackingNumber && (
                        <div className="tracking">
                          <span className="label">Tracking:</span>
                          <span className="tracking-number">{order.trackingNumber}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Favorites Tab */}
        {activeTab === 'favorites' && (
          <div className="favorites-section">
            <h2>Saved Items</h2>
            {favorites.length === 0 ? (
              <div className="empty-state">
                <p>You haven't saved any items yet.</p>
                <button onClick={() => navigate('/products')} className="continue-shopping">
                  Browse Products
                </button>
              </div>
            ) : (
              <div className="favorites-grid">
                {favorites.map((product) => (
                  <div key={product._id} className="favorite-card">
                    <div className="favorite-image">
                      {product.images && product.images[0] ? (
                        <img src={product.images[0]} alt={product.name} />
                      ) : (
                        <div className="no-image">No Image</div>
                      )}
                    </div>
                    <div className="favorite-info">
                      <h3>{product.name}</h3>
                      <p className="price">R{product.price?.toFixed(2)}</p>
                      <p className="culture">{product.culturalOrigin}</p>
                      <button
                        className="add-to-cart-btn"
                        onClick={() => navigate(`/products/${product._id}`)}
                      >
                        View Product
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div className="preferences-section">
            <h2>Preferences</h2>
            <div className="preferences-card">
              <h3>Email Notifications</h3>
              <div className="pref-row">
                <div className="pref-info">
                  <span className="pref-label">Order Updates</span>
                  <span className="pref-desc">Receive emails about your order status, shipping and delivery</span>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={preferences.emailOrders}
                    onChange={() => handlePreferenceChange('emailOrders')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="pref-row">
                <div className="pref-info">
                  <span className="pref-label">Promotions &amp; Deals</span>
                  <span className="pref-desc">Special offers, new arrivals and Heritage Hub news</span>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={preferences.emailPromos}
                    onChange={() => handlePreferenceChange('emailPromos')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div className="preferences-card">
              <h3>SMS Notifications</h3>
              <div className="pref-row">
                <div className="pref-info">
                  <span className="pref-label">Order SMS Alerts</span>
                  <span className="pref-desc">Get SMS updates when your order ships or is delivered</span>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={preferences.smsOrders}
                    onChange={() => handlePreferenceChange('smsOrders')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Wallet Tab */}
        {activeTab === 'wallet' && (
          <div className="wallet-section">
            <div className="wallet-header">
              <h2>Wallet</h2>
              {!addingCard && (
                <button className="add-card-btn" onClick={() => setAddingCard(true)}>
                  + Add Card
                </button>
              )}
            </div>
            <p className="wallet-note">
              💳 Save card details for faster checkout. Your card information is stored securely and is never shared.
            </p>

            {addingCard && (
              <form className="add-card-form" onSubmit={handleAddCard}>
                <h3>Add New Card</h3>
                <div className="form-row">
                  <label>Card Nickname (e.g. "My FNB Card")</label>
                  <input
                    type="text"
                    value={cardForm.nickname}
                    onChange={e => setCardForm({ ...cardForm, nickname: e.target.value })}
                    placeholder="Optional nickname"
                  />
                </div>
                <div className="form-row">
                  <label>Card Type</label>
                  <select
                    value={cardForm.type}
                    onChange={e => setCardForm({ ...cardForm, type: e.target.value })}
                  >
                    <option>Visa</option>
                    <option>Mastercard</option>
                    <option>American Express</option>
                  </select>
                </div>
                <div className="form-row">
                  <label>Last 4 digits</label>
                  <input
                    type="text"
                    value={cardForm.last4}
                    onChange={e => setCardForm({ ...cardForm, last4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    placeholder="1234"
                    maxLength={4}
                    required
                  />
                </div>
                <div className="form-row">
                  <label>Expiry Date</label>
                  <input
                    type="text"
                    value={cardForm.expiry}
                    onChange={e => setCardForm({ ...cardForm, expiry: e.target.value })}
                    placeholder="MM/YY"
                    maxLength={5}
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="save-profile-btn">Save Card</button>
                  <button type="button" className="cancel-btn" onClick={() => setAddingCard(false)}>Cancel</button>
                </div>
              </form>
            )}

            {savedCards.length === 0 && !addingCard ? (
              <div className="empty-state">
                <p>No saved cards yet.</p>
              </div>
            ) : (
              <div className="cards-list">
                {savedCards.map(card => (
                  <div key={card.id} className="card-item">
                    <div className="card-icon">{card.type === 'Visa' ? '💳' : card.type === 'Mastercard' ? '💳' : '💳'}</div>
                    <div className="card-details">
                      <span className="card-type">{card.type}</span>
                      <span className="card-number">•••• •••• •••• {card.last4}</span>
                      {card.nickname && <span className="card-nickname">{card.nickname}</span>}
                      <span className="card-expiry">Expires {card.expiry}</span>
                    </div>
                    <button className="remove-card-btn" onClick={() => handleRemoveCard(card.id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ShopperAccount;
